// ADR 29.9.26ap §5 — the doors with a face: a brand side beside the form at
// medium and up.
//
// On the route, not in the panels: `SignInPanel` and `RegisterPanel` are also
// inline doors on the shared plan (5.9.26t), where a second logo would sit
// inside someone else's page. Only `SignedOutOnly`, which only the two door
// routes use, wraps its panel in this.
//
// **2 : 3, not halves.** The doors are immersive and signed out, so the cell is
// the window minus the shell's padding, capped at the route's `prose`. At the
// 120 % root the form column is ~446 px at 820 px — more than a phone's form in
// phone-equivalent rem — and at 1440 px it reaches the form frame's own
// `max-w-md`. Equal halves would be narrower than any phone.
//
// No `PageFrame` here: the routes carry no `handle.frame`, so the shell's cell
// is already `prose`, and the panels keep 29.9.26al's `form` frame.
//
// §7 — at compact the wrapper is a classless `div` and slot 0 is `null`, so the
// panel keeps its sibling index at every class: a resize remounts neither the
// typed credentials nor GIS's button host.

import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { byWindowClass } from '../layout/byWindowClass'
import { useWindowClassValue } from '../layout/windowClassContext'
import { InstallButton } from '../pwa/InstallButton'

const SPLIT = 'grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] items-center gap-8 py-8'

/** Under the form at compact; across both columns, centred, at medium and up. */
const INSTALL_PLACEMENT = { compact: 'mx-auto mt-4 w-fit', medium: 'col-span-full justify-self-center' }

/**
 * The icon, the wordmark and one translated product line. Not a landmark and
 * not a heading: the door's `h2` is the page's heading. The icon is decorative
 * because the wordmark beside it says the same thing, and the wordmark is a
 * literal for `RouteAnnouncer`'s `BRAND` reason — a proper noun.
 */
function BrandSide() {
  const { t } = useTranslation()
  return (
    <div className="space-y-3 rounded-card bg-surface-container p-8">
      <img src="/AppIcon.webp" width={64} height={64} alt="" />
      <p className="text-headline-sm">Lingara</p>
      <p className="text-body-lg text-ink-muted">{t('plan_benefits_media_description')}</p>
    </div>
  )
}

/**
 * Slot 0 is `BrandSide` or `null`, slot 1 the door, at every class. ADR
 * 6.10.26a §1 — slot 2 is the consent-independent install button, appended
 * last so the first two keep their indices; at medium it spans the grid.
 */
export function AuthSplit({ children }: { children: ReactNode }) {
  const { sizeClass } = useWindowClassValue()
  return (
    <div className={byWindowClass(sizeClass, { compact: '', medium: SPLIT })}>
      {byWindowClass(sizeClass, { compact: null, medium: <BrandSide /> })}
      {children}
      <InstallButton surface="door" className={byWindowClass(sizeClass, INSTALL_PLACEMENT)} />
    </div>
  )
}
